import { NgClass } from "@angular/common";
import {
	Component,
	EventEmitter,
	Output,
	WritableSignal,
	effect,
	signal,
} from "@angular/core";
import { TranslocoPipe } from "@jsverse/transloco";
import { NzButtonComponent } from "ng-zorro-antd/button";
import { NzIconDirective } from "ng-zorro-antd/icon";
import { NzMessageService } from "ng-zorro-antd/message";
import { NzSpinComponent } from "ng-zorro-antd/spin";
import { NzUploadComponent, NzUploadFile } from "ng-zorro-antd/upload";
import { CdnApi } from "../../../domain/api/cdn.api";
import {
	IMAGE_MIME_TYPES,
	acceptFor,
	fileTypeNotAllowedMessage,
	isAllowedFileType,
	resolveMimeType,
	toUploadFile,
	uploadErrorMessage,
} from "../../../domain/helper/upload.helper";
import { uuid } from "../../../domain/helper/uuid.helper";
import { FormFile } from "../../../domain/model/file.model";
import { BaseComponent } from "./base.component";

@Component({
	selector: "app-image-upload-profile",
	imports: [
		NgClass,
		NzUploadComponent,
		NzSpinComponent,
		NzButtonComponent,
		NzIconDirective,
		TranslocoPipe,
	],
	template: `

    <div [ngClass]="{'invisible': imageLoading}" class="flex flex-row justify-center">
      <nz-upload
        class="min-w-0 max-w-full"
        [nzDisabled]="imageLoading"
        (nzChange)="onUploadChange()"
        [nzShowButton]="fileList.length < 1"
        [(nzFileList)]="fileList"
        [nzAccept]="imageAccept"
        [nzBeforeUpload]="beforeUpload">
        @if (imageLoading) {
          <nz-spin nzSimple/>
        }
        <button nz-button class="m-auto" (click)="$event.preventDefault()">
          <span nz-icon nzType="upload"></span>
          {{ 'upload.image.uploadButton' | transloco }}
        </button>
      </nz-upload>
    </div>

  `,
})
export class ImageUploadProfileComponent extends BaseComponent {
	@Output() fileUploaded = new EventEmitter<string>();
	fileList: NzUploadFile[] = [];
	file: WritableSignal<NzUploadFile | undefined> = signal(undefined);
	imageLoading = false;
	imageTypes = IMAGE_MIME_TYPES;
	imageAccept = acceptFor(IMAGE_MIME_TYPES);

	/**
	 * Keeps an accepted file for the component's own upload and rejects the
	 * others with a message; always false so nz-upload never posts anything.
	 */
	readonly beforeUpload = (file: NzUploadFile): boolean => {
		if (isAllowedFileType(file, this.imageTypes)) {
			this.fileList = [...this.fileList, toUploadFile(file)];
			this.onUploadChange();
		} else {
			this.message.error(fileTypeNotAllowedMessage());
		}
		return false;
	};

	constructor(
		private readonly cdnApi: CdnApi,
		private readonly message: NzMessageService,
	) {
		super();
		effect(async () => {
			if (this.file()) {
				console.log(this.file());
				await this.upload();
			}
		});
	}

	onUploadChange() {
		if (this.fileList.length === 0) return;
		if (this.fileList[0].status === "removed") {
			this.fileUploaded.emit("");
			this.file.set(undefined);
		} else {
			this.file.set(this.fileList[0]);
		}
	}

	async upload() {
		try {
			this.imageLoading = true;
			if (!this.file()) return;

			const uploadFile = this.file()!;
			const mimeType = resolveMimeType(uploadFile, this.imageTypes);
			if (!uploadFile.originFileObj || !uploadFile.name || !mimeType) return;

			const file: FormFile = {
				file: uploadFile.originFileObj,
				filename: uploadFile.name,
				mimeType,
			};

			const fileId = uuid();
			await this.cdnApi.uploadProfile(file, fileId);
			this.fileUploaded.emit(fileId);
		} catch (e) {
			this.message.error(uploadErrorMessage(e));
			this.fileList = [];
		} finally {
			this.imageLoading = false;
			this.file.set(undefined);
		}
	}
}
